import path from 'node:path';
import pc from 'picocolors';
import { ApiClient } from '../client.js';
import { DEFAULT_PRODUCT_NAME } from '../constants.js';
import { readAppConfig } from '../lib/appConfig.js';
import { resolveProfile } from '../profiles.js';
import { emitJson, fail, human, note, ok, type Io } from '../output.js';
import { waitForDeploy, type DeployRecord } from './deploy.js';

/**
 * `ghosty rollback` — put a previous successful deploy's version back live.
 * Release-only: the platform re-points the app at the image that deploy
 * built (no rebuild, no database changes — schema migrations are forward-
 * only, which is the one thing a rollback can NOT undo).
 */

interface RollbackOptions {
  dir?: string;
  json?: boolean;
  adminUrl?: string;
  /** Deploy id or commit-sha prefix to roll back to; default = the previous successful version. */
  to?: string;
}

/**
 * Choose the rollback target among SUCCESS deploys (newest first, as the API
 * returns them). With `to`, match a deploy id or a commit-sha prefix; without
 * it, the newest successful deploy whose sha differs from the current one.
 * Exported for tests.
 */
export function pickRollbackTarget(
  deploys: DeployRecord[],
  to?: string,
): DeployRecord | undefined {
  const successes = deploys.filter((d) => d.status === 'SUCCESS' && d.id && d.commitSha);
  if (to) {
    return successes.find((d) => d.id === to || d.commitSha!.startsWith(to));
  }
  const current = successes[0];
  if (!current) return undefined;
  return successes.find((d) => d.commitSha !== current.commitSha);
}

export async function runRollback(options: RollbackOptions): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const dir = path.resolve(process.cwd(), options.dir ?? '.');
  const resolved = resolveProfile(options.adminUrl, undefined, dir);
  if (!resolved) fail(io, 'Not signed in. Run `ghosty login` first.', 1, 'NOT_LOGGED_IN');
  io.productName = resolved.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;
  let slug: string;
  try {
    slug = readAppConfig(dir).slug;
  } catch (err) {
    fail(io, (err as Error).message, 1, 'BAD_APP_TREE');
  }
  const client = new ApiClient(resolved.adminUrl, resolved.profile.token);

  let deploys: DeployRecord[];
  try {
    ({ deploys } = await client.get<{ deploys: DeployRecord[] }>(
      `/builder-api/apps/${slug}/deploys?limit=50`,
    ));
  } catch (err) {
    fail(io, (err as Error).message, 1, 'API_ERROR');
  }

  const target = pickRollbackTarget(deploys, options.to);
  if (!target) {
    fail(
      io,
      options.to
        ? `No successful deploy matches "${options.to}". Run \`ghosty deploys\` to see the history.`
        : 'No previous version to roll back to — the current version is the only successful deploy.',
      1,
      'NO_ROLLBACK_TARGET',
    );
  }

  let accepted: { ok: boolean; buildId: string; commitSha: string };
  try {
    accepted = await client.post<{ ok: boolean; buildId: string; commitSha: string }>(
      `/builder-api/apps/${slug}/deploys/${target.id}/rollback`,
    );
  } catch (err) {
    fail(io, (err as Error).message, 1, 'ROLLBACK_REJECTED');
  }

  if (io.json) {
    emitJson({ event: 'rolling_back', slug, buildId: accepted.buildId, commitSha: accepted.commitSha });
  } else {
    note(io, `Rolling ${slug} back to ${accepted.commitSha.slice(0, 7)}…`);
  }

  // Match by build id ONLY — the original deploy of this sha is also in the
  // history and would read as an instant (false) success.
  await waitForDeploy(client, io, slug, { buildId: accepted.buildId });

  if (io.json) {
    emitJson({ ok: true, slug, buildId: accepted.buildId, commitSha: accepted.commitSha });
    return;
  }
  human(io);
  ok(io, `${pc.bold(slug)} rolled back to ${accepted.commitSha.slice(0, 7)}.`);
  human(io, '  Database changes made after that deploy are not undone.');
  human(io);
}
